import { P } from '../world/player';
import { mechEvent } from '../entities/mech-anim';
/* ============================================================
   經驗值與等級（M4）
   ============================================================

   M3 把整個數值升級層拔掉，理由是「熱量制讓增壓電容當場失效」。
   熱量在同一天退場之後那個理由就不存在了，而 2026-08-01 轉向倖存者類
   讓成長重新變成核心迴圈 —— 所以這一層裝回來，但形狀跟 0.1 不同。

   ---------- 曲線形狀 ----------

   0.1 用二次式：`xpNeed = 8 + lv×4.2 + lv²×0.55`。
   問題是 lv 30 時單級要 629 點 —— **後期等於停止成長**，而倖存者類的後半
   還要靠升級維持節奏。

   改用**分段線性**：每級的增量在幾個斷點跳一次。前期升得快（給糖）、
   後期慢但不會死。這是倖存者類普遍的做法。

   ---------- 2026-08-05（P5）：試著校準，結論是「先不要動這裡」 ----------

   下面的常數仍然是起始值。P5 這一輪**實測過把它改成 01 §五的目標分佈**
   （0–2 分 5 級、2–8 分 11 級、8–15 分 12 級、15–20 分 8 級，
   由 `pnpm pacing` 的 DPS 曲線積分反推），結果是五顆種子的存活中位數
   從 7.2 分掉到 3.5 分、單局總升級 35 → 6。

   曲線本身沒錯 —— 無敵探針用那組數字跑滿 20 分鐘會落在 3 / 9 / 12 / 6 級，
   形狀跟 01 幾乎一樣，只是整條低 17%。真正的原因是一條**耦合**：
   這款遊戲玩家的戰力幾乎全部來自升級，而難度只吃時鐘
   （`sim/spawn.ts` 查 `data/pacing.ts` 的表）。於是「把升級曲線調慢」
   在數學上等同於「把難度調高」，兩者不是獨立的旋鈕 —— 01 那組分佈要成立，
   前提是第 3–6 分鐘的威脅也一起降下來，那是比 P5 更大的一次重配平。

   所以現況是刻意的：**單局總升級 35 次已經落在 01 的 32–38 帶寬內**
   （P5 把經驗綁到敵人實際血量之後才做到的，之前是 28）。
   沒對上的是分佈 —— 前 2 分鐘會升 17 級而 01 要 5 級，這一項留在交接的待辦。

   量測工具：`pnpm pace`（每一級的時間戳）、`pnpm metrics` 第 2 區（分階段）、
   `pnpm pacing` 第 1 區（跑得完 20 分鐘的那一版）。 */

/* ---------- 2026-08-05 第二輪：整條曲線攤平（照 01 §五的分佈反推） ----------

   使用者的判斷（原話）：「升級在前期太頻繁，讓玩家產生打斷感很嚴重。
   把整場升級數拉到 35 左右但是能比較平均的。」

   實測的時間戳說他是對的 —— `pnpm pace` 種子 5eed1234：

     lv2@13s  lv3@18s  lv4@24s  lv5@26s  lv6@29s  lv7@37s  lv8@43s  lv9@57s

   **前 30 秒升五級、平均每 6 秒打斷一次**，而每一次都會把畫面停下來發卡。
   `pnpm metrics` 的分階段是 `0–2分 13／2–8分 22／8–15分 0／15–20分 0`。

   ## 前期太快與後期沒東西是同一個病

   35 次有效選擇在第 8 分鐘之前就全部用完 → 5 武裝 6 被動全部滿級 →
   抽卡池歸零（`pnpm pacing` 第 6 節：第 7 分鐘之後 `draftPoolIds()` 是 0）→
   之後的升級被 `ui/levelup.ts` 的空池分支整批丟掉。**把前期攤平，後期自然就有東西可選。**

   > 2026-08-05 補：那個「整批丟掉」已經修掉了（改成發補給，見
   > `ui/levelup.ts` 的 `takeSupply` 呼叫處與 `loadout.test.ts` 的「抽卡池見底」）。
   > 但**攤平曲線仍然是對的**：補給只是止血，它不能取代真的有東西可選。

   下面那一段（第一版只砍後段）留著當紀錄：

     `ui/levelup.ts` 當時是 → `if (!currentChoices.length) { P.pendingLv = 0; return; }`

   抽卡池空了（5 武裝 6 被動全滿級，第 7–8 分鐘就會發生，見 `pnpm pacing` 第 6 節）
   之後，**待處理的升級會被整批歸零** —— 選單不出現、玩家什麼都沒拿到。
   實測（`pnpm metrics` 五種子）：一場 130 級，而真的給出選項的只有 **35 級**，
   分階段是 `0–2分 13／2–8分 22／8–15分 0／15–20分 0`。

   所以「總升級 35 落在 01 的 32–38 帶寬內 ✅」那句話是假的 —— 它量的是
   「玩家做了幾次選擇」，而分母（升了幾級）是它的四倍。

   ## 為什麼砍掉是零代價的

   `P.lv` 的消費端只有快照、HUD 與量測（`grep -rn 'P\.lv'`）—— **模擬層沒有
   任何東西讀它**。玩家的戰力全部來自「選了什麼」，不是等級數字本身。
   所以砍掉那 95 級空轉不會動到戰力曲線，也就不會踩上面那條
   「調升級曲線等於調難度」的耦合。

   ## 分段怎麼定（不是手感，是反推的）

   把 baseline 報表的等級曲線積分回**經驗收入**（2 分 632、8 分 21k、
   15 分 110k、19.5 分 215k），再要求曲線在四個檢查點上剛好落在
   01 §五的目標分佈（0–2 分 5 級、2–8 分 11、8–15 分 12、15–20 分 8
   → 累積 lv6 / lv17 / lv29 / lv37）。解出來就是下面的 `CURVE_SEGMENTS`：

     段        每級增量   舊值    這一段要吸收的經驗
     1–5 級       59       6           632
     6–16 級     318      16        20,847
     17–28 級    648      30        88,380
     29+ 級      432      45       104,706

   最後一段的增量**比前一段小**是對的：第 16 分鐘之後擊殺率從 85/秒掉到
   50/秒（怪潮結束、Boss 佔場），收入本身在減速。

   驗算（同一條收入曲線）：lv6@2分、lv17@8分、lv29@15分、lv37@19.5分。

   ⚠️ **這個改動必然會壓低前期戰力**，因為玩家的戰力全部來自「選了什麼」，
   而難度只吃時鐘 —— 這正是上面 P5 那一輪記的耦合。上一次直接套 01 分佈
   死在這裡（存活 7.2 → 3.5 分），差別是那時候存活中位數只有 7.2 分，
   現在是 14.8 分（十種子），有餘裕吃這個代價。
   **驗收條件寫在這裡**：`pnpm metrics` 十種子的存活中位數不得低於 8 分
   （07 的「首局 5–8 分」是目標，低於 8 分就要回來降前期密度 `data/pacing.ts`）。

   膝點（`CURVE_KNEE`）留著當**跑不掉的保險**：新曲線 20 分鐘落在 37 級，
   所以它平常不會被觸發；只有難度或收入之後又被調高時才會擋住暴衝。 */

/** 分段：[起始等級, 每級增量]。查表時取「不大於當前等級」的最後一段。 */
export var CURVE_BASE = 8;
export var CURVE_SEGMENTS = [
  [1, 59],     // 1–5 級：第一級仍然很快（8 點），但不再是每 6 秒一次
  [6, 318],    // 6–16 級：目標第 8 分鐘到 17 級
  [17, 648],   // 17–28 級：目標第 15 分鐘到 29 級
  [29, 432]    // 29+ 級：收入在第 16 分鐘之後減速，所以增量比上一段小
];
/** 膝點：這一級之後改成等比成長。**這是保險不是旋鈕** ——
    新曲線 20 分鐘落在 37 級，正常對局碰不到它。 */
export var CURVE_KNEE = 38;
/** 膝點之後每一級的倍率。 */
export var CURVE_RATIO = 1.3;

/** 第 lv 級所在分段的每級增量。 */
function incAt(lv) {
  var step = CURVE_SEGMENTS[0][1];
  for (var i = 0; i < CURVE_SEGMENTS.length; i++) {
    if (lv >= CURVE_SEGMENTS[i][0]) step = CURVE_SEGMENTS[i][1];
  }
  return step;
}

/** 從 lv 升到 lv+1 需要多少經驗。

   寫成明顯正確的累加而不是閉式解：等級最多 40 左右、每次升級只算一次，
   成本可忽略，而閉式解要處理分段邊界，寫錯了不會有任何測試會紅。 */
export function xpForLevel(lv) {
  var need = CURVE_BASE;
  /* 膝點之前照分段累加；膝點之後改等比。**累加的上界要夾在膝點**，
     不夾的話等比會疊在「一路線性長上去」的基準上，倍率的意義就變了。 */
  var top = lv < CURVE_KNEE ? lv : CURVE_KNEE;
  for (var L = 1; L < top; L++) need += incAt(L);
  if (lv > CURVE_KNEE) need *= Math.pow(CURVE_RATIO, lv - CURVE_KNEE);
  return Math.max(1, Math.round(need));
}

export function resetLevel() {
  P.lv = 1; P.xp = 0; P.xpNeed = xpForLevel(1); P.pendingLv = 0;
}

/** 拾取經驗球時呼叫。可能一次跳好幾級（後期的大波），所以用 while。 */
export function gainXp(n) {
  /* `growth`（被動「資料同化」，對應 VS 的 Crown）**乘在獲得端而不是需求端**。
     改 xpNeed 的話升級曲線本身就變了，M4/M9 的校準基準會跟著漂；
     乘在獲得端等於「整場的時間軸被壓縮」，曲線不動，量測仍然比得了。 */
  P.xp += n * ((P.s && P.s.growth) || 1);
  var guard = 0;
  while (P.xp >= P.xpNeed && guard++ < 50) {
    P.xp -= P.xpNeed;
    P.lv++;
    P.xpNeed = xpForLevel(P.lv);
    P.pendingLv++;
    mechEvent(P.rig, 'levelup');
  }
}

/** 0–1，給 HUD 的經驗條用。 */
export function xpRatio() { return P.xpNeed > 0 ? P.xp / P.xpNeed : 0; }
